import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Trash2, ExternalLink } from "lucide-react";
import type { NotificationSettings } from "@/hooks/useNotificationSettings";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface TeamsConfigProps {
  settings: NotificationSettings;
  onSave: (updates: Partial<NotificationSettings>) => Promise<void>;
}

export function TeamsConfig({ settings, onSave }: TeamsConfigProps) {
  const [url, setUrl] = useState(settings.teams_webhook_url || "");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testSent, setTestSent] = useState(false);

  useEffect(() => {
    setUrl(settings.teams_webhook_url || "");
  }, [settings]);

  const handleSave = async () => {
    try {
      await onSave({ teams_webhook_url: url || null, teams_enabled: url ? true : false });
      toast.success("Microsoft Teams settings saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save Teams settings");
    }
  };

  const handleDisconnect = async () => {
    try {
      await onSave({ teams_webhook_url: null, teams_enabled: false });
      setUrl("");
      setVerified(false);
      setTestSent(false);
      toast.success("Teams webhook disconnected.");
    } catch {
      toast.error("Failed to disconnect.");
    }
  };

  // Verify Connection only validates the URL/credentials
  const handleVerify = async () => {
    if (!url) return toast.error("Enter a webhook URL first");
    setVerifying(true);
    setVerified(false);
    try {
      // Simulate credential/URL pattern validation or ping checking
      if (!url.startsWith("https://") || !url.includes("webhook")) {
        throw new Error("Invalid Microsoft Teams webhook URL structure.");
      }
      setVerified(true);
      toast.success("Teams Connection Verified");
    } catch (e: any) {
      toast.error(e.message || "Failed to verify connection");
    } finally {
      setVerifying(false);
    }
  };

  // Send Test Alert actually triggers the delivery check
  const handleSendTestAlert = async () => {
    if (!url) return toast.error("Enter and verify connection first");
    setSendingTest(true);
    setTestSent(false);
    try {
      const { data, error } = await supabase.functions.invoke("test-integration", {
        body: { type: "teams", url },
      });
      if (error || (data && data.error)) {
        toast.error(error?.message || data?.error || "Failed to send test alert");
      } else {
        setTestSent(true);
        toast.success("Test alert sent to Teams");
      }
    } catch {
      toast.error("Failed to deliver Teams test alert");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm font-black uppercase tracking-tight">Microsoft Teams Integration</p>
          <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Route incident alerts to your Teams channels</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Quick Setup Guide */}
        <div className="border-2 border-primary/20 bg-primary/5 p-4 rounded-none space-y-3 group hover:border-primary transition-colors">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase">Quick Setup Guide</span>
          </div>
          <div className="bg-muted/30 border border-foreground/10 p-3 text-[10px] font-bold uppercase tracking-tight text-muted-foreground space-y-1.5">
            <p className="text-foreground font-black">Follow these steps:</p>
            <p className="flex gap-2"><span>1.</span> Open Teams → target channel → "..." → Connectors.</p>
            <p className="flex gap-2"><span>2.</span> Search for "Incoming Webhook" → Add → Configure.</p>
            <p className="flex gap-2"><span>3.</span> Name it "Watchora Alerts" and click Create.</p>
            <p className="flex gap-2"><span>4.</span> Copy the generated Webhook URL and paste below.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-none border-2 border-foreground bg-background font-black uppercase text-[9px] gap-1.5"
            onClick={() => window.open("https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook", "_blank")}
          >
            <ExternalLink className="h-3 w-3" />
            Microsoft Docs
          </Button>
        </div>

        {/* Webhook URL Input */}
        <div className="border-2 border-border p-4 rounded-none space-y-4">
          <div className="space-y-1">
            <span className="text-[10px] font-black uppercase opacity-40">Incoming Webhook URL</span>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-black uppercase opacity-60">Webhook URL</Label>
            <Input
              value={url}
              onChange={(e) => { setUrl(e.target.value); setVerified(false); setTestSent(false); }}
              placeholder="https://your-org.webhook.office.com/webhookb2/..."
              className="h-10 rounded-none border-2 border-foreground font-mono text-[10px] focus-visible:ring-primary shadow-brutal-sm"
            />
          </div>
          {url && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[9px] font-black uppercase text-status-down hover:bg-status-down/10 gap-1.5 h-8 mt-2"
                >
                  <Trash2 className="h-3 w-3" /> Disconnect Webhook
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="glass-strong rounded-none border-4 border-foreground bg-background shadow-brutal p-6">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-base font-black uppercase tracking-tight">Disconnect Teams</AlertDialogTitle>
                  <AlertDialogDescription className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-2">
                    This will remove your Teams webhook. Incident alerts will no longer be delivered to Teams.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="mt-4 gap-2 flex-row justify-end">
                  <AlertDialogCancel className="rounded-none border-2 border-foreground bg-background text-[10px] font-black uppercase tracking-widest shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all mt-0">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDisconnect}
                    className="rounded-none border-2 border-foreground bg-status-down text-white hover:bg-foreground hover:text-background font-black uppercase text-[10px] shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
                  >
                    Disconnect
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Button onClick={handleSave} className="h-9 rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase text-[10px] shadow-brutal-sm hover:bg-primary transition-all">
          Save Configuration
        </Button>
        <Button
          variant="outline"
          onClick={handleVerify}
          disabled={verifying || !url}
          className="h-9 rounded-none border-2 border-foreground bg-background font-black uppercase text-[10px] shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
        >
          {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          {verifying ? "Verifying..." : "Verify Connection"}
        </Button>
        {verified && (
          <Button
            variant="outline"
            onClick={handleSendTestAlert}
            disabled={sendingTest}
            className="h-9 rounded-none border-2 border-primary bg-primary/10 font-black uppercase text-[10px] shadow-brutal-sm hover:bg-primary/20 transition-all"
          >
            {sendingTest ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            {sendingTest ? "Sending Alert..." : "Send Test Alert"}
          </Button>
        )}
        {verified && (
          <div className="flex items-center gap-1.5 ml-auto bg-status-up/10 px-3 py-1 border-2 border-status-up shadow-brutal-sm">
            <CheckCircle2 className="h-3 w-3 text-status-up" />
            <span className="text-[10px] font-black text-status-up uppercase">
              {testSent ? "Test Sent" : "Verified"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
